import { useCallback, useEffect } from 'react';
import { useMap } from 'react-leaflet';

import { useActions, useAppState } from '@/overmind';
import type { ImageSize } from '@/overmind/effects/adapters/baseadapter';

import { useShouldGridImageBeVisible } from './useShouldGridImageBeVisible';

function useCallbackOnMapMove(callback: () => void) {
  const map = useMap();
  useEffect(() => {
    if (map) {
      map.addEventListener('moveend', callback);
    }
    return () => {
      if (map && map.hasEventListeners('moveend')) {
        map.removeEventListener('moveend', callback);
      }
    };
  }, [map, callback]);
}

function useGetGridImage() {
  const { loadMapImage } = useActions().arcgis.mapImage;
  const shouldFetchNewImage = useShouldGridImageBeVisible();
  const map = useMap();
  const mapConfig =
    useAppState().arcgis.config.matches('LOADED')?.value.mapSource;

  return useCallback(() => {
    if (map && shouldFetchNewImage) {
      const bounds = map.getBounds();
      const imageSize: ImageSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      const orderedLayerIds =
        (map.getZoom() > 18
          ? mapConfig?.layerIds
          : mapConfig?.overviewLayerIds) ?? [];
      loadMapImage({ bounds, size: imageSize, orderedLayerIds });
    }
  }, [map, mapConfig, shouldFetchNewImage, loadMapImage]);
}

function useCallbackOnDependecyUpdate(callback: () => void) {
  const map = useMap();
  const currentTenant = useAppState().tenants.currentTenant;
  const showGridImage = useShouldGridImageBeVisible();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, [map, currentTenant, showGridImage]);
}

export function useAutoFetchNewGridImages() {
  const getNewGridImage = useGetGridImage();

  useCallbackOnDependecyUpdate(getNewGridImage);
  useCallbackOnMapMove(getNewGridImage);
}
